// GalleryAdmin.jsx
import { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function GalleryAdmin() {
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");

  // Validation errors
  const [errors, setErrors] = useState({});

  // Loading states
  const [uploadLoading, setUploadLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Gallery
  const [galleryImages, setGalleryImages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Admin filter
  const [adminFilter, setAdminFilter] = useState("All");

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const q = query(collection(db, "gallery"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setGalleryImages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Failed to fetch gallery:", e);
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    if (!file && !editingId) newErrors.file = "Please select an image *";
    if (!title.trim()) newErrors.title = "Title is required *";
    if (!description.trim()) newErrors.description = "Description is required *";
    if (!category || category === "default") newErrors.category = "Please select category *";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear validation on input
  const handleChange = (field, value) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
    switch (field) {
      case "file":
        setFile(value);
        break;
      case "replaceFile":
        setReplaceFile(value);
        break;
      case "title":
        setTitle(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "category":
        setCategory(value);
        break;
      default:
        break;
    }
  };

  // UPLOAD
  const handleUpload = async () => {
    if (!validateForm()) return;

    try {
      setUploadLoading(true);
      const fileName = file.name.replace(/\s/g, "_");
      const storageRef = ref(storage, `gallery/${Date.now()}_${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(snapshot.ref);

      await addDoc(collection(db, "gallery"), {
        imageUrl,
        title,
        description,
        category,
        createdAt: serverTimestamp(),
      });

      resetForm();
      fetchGalleryImages();
    } catch (e) {
      console.error(e);
    } finally {
      setUploadLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setTitle("");
    setDescription("");
    setCategory("");
    setErrors({});
    setShowForm(false);
  };

  // UPDATE
  const handleUpdate = async (img) => {
    if (!validateForm()) return;

    try {
      setUpdateLoading(true);
      let imageUrl = img.imageUrl;

      if (replaceFile) {
        const fileName = replaceFile.name.replace(/\s/g, "_");
        const storageRef = ref(storage, `gallery/${Date.now()}_${fileName}`);
        const snapshot = await uploadBytes(storageRef, replaceFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      await updateDoc(doc(db, "gallery", img.id), {
        title,
        description,
        category,
        imageUrl,
      });

      setEditingId(null);
      setReplaceFile(null);
      setErrors({});
      fetchGalleryImages();
    } catch (e) {
      console.error(e);
    } finally {
      setUpdateLoading(false);
    }
  };

  // DELETE
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "gallery", id));
      setDeleteConfirmId(null);
      fetchGalleryImages();
    } catch (e) {
      console.error(e);
    }
  };

  const filteredImages =
    adminFilter === "All"
      ? galleryImages
      : galleryImages.filter((img) => img.category === adminFilter);

  return (
    <div className="p-4 space-y-8">

      <h1 className="text-3xl font-bold text-brand-dark text-center">Gallery Manager</h1>

      {/* Filter & Add */}
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-brand-dark text-white px-6 py-2 rounded-md"
        >
          {showForm ? "Close Upload" : "Add Gallery Image"}
        </button>

        <select
          value={adminFilter}
          onChange={(e) => setAdminFilter(e.target.value)}
          className="border px-4 py-2 rounded"
        >
          <option>All</option>
          <option>Team</option>
          <option>Projects</option>
          <option>Customer Visits</option>
          <option>Documentation</option>
          <option>Celebrations</option>
        </select>
      </div>

      {/* Upload Form */}
      {showForm && (
        <div className="bg-white shadow rounded-lg p-6 max-w-xl mx-auto space-y-4">
          <div>
            <label className="font-medium block">
              Image <span className="text-red-600">*</span>
            </label>
            <input type="file" accept="image/*" onChange={(e) => handleChange("file", e.target.files[0])} />
            {file && <p className="text-sm break-words">{file.name}</p>}
            {errors.file && <p className="text-red-600 text-xs mt-1">{errors.file}</p>}
          </div>

          <div>
            <label className="font-medium block">
              Category <span className="text-red-600">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => handleChange("category", e.target.value)}
              className="w-full border p-2 rounded"
            >
              <option value="default">Please select category</option>
              <option>Team</option>
              <option>Projects</option>
              <option>Customer Visits</option>
              <option>Documentation</option>
              <option>Celebrations</option>
            </select>
            {errors.category && <p className="text-red-600 text-xs mt-1">{errors.category}</p>}
          </div>

          <div>
            <label className="font-medium block">
              Title <span className="text-red-600">*</span>
            </label>
            <input
              className="w-full border p-2 rounded"
              value={title}
              onChange={(e) => handleChange("title", e.target.value)}
            />
            {errors.title && <p className="text-red-600 text-xs mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="font-medium block">
              Description <span className="text-red-600">*</span>
            </label>
            <textarea
              className="w-full border p-2 rounded"
              value={description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
            {errors.description && <p className="text-red-600 text-xs mt-1">{errors.description}</p>}
          </div>

          <button
            onClick={handleUpload}
            disabled={uploadLoading}
            className="w-full bg-brand-dark text-white py-2 rounded"
          >
            {uploadLoading ? "Uploading..." : "Upload Image"}
          </button>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredImages.map((img) => (
          <div key={img.id} className="relative bg-white rounded-lg shadow">
            <button
              onClick={() => {
                setEditingId(editingId === img.id ? null : img.id);
                setTitle(img.title);
                setDescription(img.description);
                setCategory(img.category);
                setReplaceFile(null);
                setErrors({});
              }}
              className="absolute top-2 right-3 text-2xl text-brand-dark z-50"
            >
              ⋮
            </button>

            <img src={img.imageUrl} className="w-full h-48 object-cover rounded-t-lg" />

            {/* Edit Menu */}
            {editingId === img.id && (
              <div className="absolute top-10 right-3 bg-white shadow-lg rounded p-4 z-50 w-64">
                <div>
                  <label className="font-medium block">
                    Image <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleChange("replaceFile", e.target.files[0])}
                  />
                  {replaceFile && (
                    <p
                      className={`text-xs mt-1 break-words ${replaceFile.name.length > 20 ? "cursor-help underline" : ""}`}
                      title={replaceFile.name.length > 20 ? replaceFile.name : ""}
                    >
                      {replaceFile.name.length > 20 ? replaceFile.name.slice(0, 20) + "..." : replaceFile.name}
                    </p>
                  )}
                  {errors.file && <p className="text-red-600 text-xs mt-1">{errors.file}</p>}
                </div>

                <div>
                  <label className="font-medium block">
                    Title <span className="text-red-600">*</span>
                  </label>
                  <input
                    className="w-full border p-1 mb-2 rounded"
                    value={title}
                    onChange={(e) => handleChange("title", e.target.value)}
                  />
                  {errors.title && <p className="text-red-600 text-xs mt-1">{errors.title}</p>}
                </div>

                <div>
                  <label className="font-medium block">
                    Description <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    className="w-full border p-1 mb-2 rounded"
                    value={description}
                    onChange={(e) => handleChange("description", e.target.value)}
                  />
                  {errors.description && <p className="text-red-600 text-xs mt-1">{errors.description}</p>}
                </div>

                <div>
                  <label className="font-medium block">
                    Category <span className="text-red-600">*</span>
                  </label>
                  <select
                    className="w-full border p-1 mb-2 rounded"
                    value={category}
                    onChange={(e) => handleChange("category", e.target.value)}
                  >
                    <option value="default">Please select category</option>
                    <option>Team</option>
                    <option>Projects</option>
                    <option>Customer Visits</option>
                    <option>Documentation</option>
                    <option>Celebrations</option>
                  </select>
                  {errors.category && <p className="text-red-600 text-xs mt-1">{errors.category}</p>}
                </div>

                <div className="flex justify-between mt-3">
                  <button
                    onClick={() => handleUpdate(img)}
                    disabled={updateLoading}
                    className="bg-brand-dark text-white px-3 py-1 rounded"
                  >
                    {updateLoading ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(img.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded space-y-4 text-center max-w-xs">
            <p>Are you sure you want to delete?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="bg-red-600 text-white px-4 py-1 rounded"
              >
                Yes
              </button>
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="bg-gray-400 text-white px-4 py-1 rounded"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}